import React from 'react';
import { Paperclip } from 'lucide-react';
import GenericFile from '@/components/ui/GenericFile';

/**
 * Shape of rows emitted by GenericFile's onChange callback.
 * Mirrors the unexported GenericFileRow type in ../GenericFile.tsx
 * (`Record<string, unknown> & { id: string }`).
 */
type AnexoShape = Record<string, unknown> & { id: string };

interface AttachmentsSectionProps {
  /** ID da entidade pai (obrigatório para salvar). */
  entityId?: string | null;
  /** Tipo da entidade (ex: 'obra_jornada', 'lancamento'). */
  entityType?: string;
  /** Prefixo para checagem de permissões. */
  recurso?: string;
  /** Modo atual do drawer (usado apenas para compatibilidade). */
  mode?: 'view' | 'edit';
  /** Se true, desabilita upload e exclusão (default: false). */
  readOnly?: boolean;
  /** Título da seção (default: 'Anexos'). */
  title?: string;
  /** Callback quando anexos mudam. */
  onAnexosChange?: (anexos: AnexoShape[]) => void;
  /** Chave para forçar reload dos anexos. */
  refreshKey?: number;
  /** Se deve mostrar o título da seção (default: true). */
  showTitle?: boolean;
  /** Layout compacto (default: false). */
  compact?: boolean;
  /** Permite editar descrição do anexo. */
  canEdit?: boolean;
  /** Permite excluir anexos. */
  canDelete?: boolean;
}

/**
 * AttachmentsSection - Seção de anexos para uso em GenericDrawer
 *
 * Wrapper do GenericFile que se integra ao padrão de seções do drawer.
 */
export default function AttachmentsSection({
  entityId,
  entityType,
  recurso,
  mode = 'view',
  readOnly = false,
  title = 'Anexos',
  onAnexosChange,
  refreshKey = 0,
  showTitle = true,
  compact = false,
  canEdit,
  canDelete,
}: AttachmentsSectionProps) {
  // readOnly prop tem precedência; se não passada, anexos são sempre editáveis
  // (independente do mode do drawer, pois adicionar anexos é ação independente)
  const isReadOnly = readOnly;

  // Se não tem entityId e não está em modo de criação, mostrar mensagem
  if (!entityId && mode !== 'edit') {
    return (
      <div style={{ padding: compact ? '12px' : '16px' }}>
        {showTitle && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px'
          }}>
            <Paperclip size={16} />
            <h4 style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 600,
              color: '#111827'
            }}>
              {title}
            </h4>
          </div>
        )}
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--muted)',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px dashed var(--panel-border)'
        }}>
          <Paperclip size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '13px' }}>
            Salve o registro para adicionar anexos
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: compact ? '12px' : '16px' }}>
      {/* Título da seção */}
      {showTitle && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <Paperclip size={16} />
          <h4 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827'
          }}>
            {title}
          </h4>
        </div>
      )}

      {/* Componente de arquivos */}
      <GenericFile
        entityId={entityId}
        entityType={entityType}
        recurso={recurso}
        readOnly={isReadOnly}
        canEdit={canEdit}
        canDelete={canDelete}
        onChange={onAnexosChange}
        refreshKey={refreshKey}
      />
    </div>
  );
}

/**
 * Tipos de entidade suportados pelo GenericFile (configurados em ENTITY_CONFIG):
 *
 * - 'obra_jornada': Anexos de jornada da obra
 *   - Tabela: obra_jornada_anexo
 *   - Bucket: obra-historico-anexos
 *   - FK: obra_jornada_id
 *
 * - 'lancamento': Anexos de lançamentos financeiros
 *   - Tabela: lancamento_anexo
 *   - Bucket: lancamento-anexos
 *   - FK: lancamento_id
 *
 * Para adicionar um novo tipo de entidade, edite ENTITY_CONFIG em GenericFile.jsx
 *
 * Exemplo de uso:
 *
 * <AttachmentsSection
 *   entityId={record?.id}
 *   entityType="obra_jornada"
 *   recurso="OBRA_JORNADA"
 *   mode={mode}
 *   title="Anexos do Histórico"
 *   onAnexosChange={(anexos) => console.log('Anexos:', anexos)}
 * />
 */
